import { useState, useEffect, useCallback } from 'react';
import {
  getUsersApi,
  exportUsersApi,
  getUserFilterOptionsApi,
  markUsersSeenApi,
} from '../api/users.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import { usePagination } from './usePagination';
import {
  defaultFilters,
  DEFAULT_CHIPS,
  toApiParams,
} from '../utils/userDirectoryFilters';
import { useNavBadgeContext } from '../context/NavBadgeContext';

export function useUserDirectory({ onboardingStatus, exportFilename }) {
  const { refreshBadges } = useNavBadgeContext();
  const [chips, setChips] = useState({ ...DEFAULT_CHIPS });
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState(() => {
    const base = defaultFilters();
    if (typeof window !== 'undefined' && window.location.search) {
      const sp = new URLSearchParams(window.location.search);
      if (sp.has('onboardingStatus')) base.onboardingStatus = sp.get('onboardingStatus');
      if (sp.has('plan')) base.plan = sp.get('plan');
      if (sp.has('verification')) base.verification = sp.get('verification');
      if (sp.has('sort')) base.sort = sp.get('sort');
      if (sp.has('search')) base.search = sp.get('search');
    }
    if (onboardingStatus) base.onboardingStatus = onboardingStatus;
    return base;
  });
  const [filterOptions, setFilterOptions] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const {
    page,
    limit,
    total,
    loading,
    setLoading,
    resetPage,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(20);

  function showToast(msg, type = 'info', durationMs = 3200) {
    setToast({ msg, type });
    setTimeout(() => setToast(null), durationMs);
  }

  const fetchUsers = useCallback(
    async (pageNum = page, filtersSnapshot = filters, chipsSnapshot = chips) => {
      setLoading(true);
      setError(null);
      const extraParams = onboardingStatus ? { onboardingStatus } : {};
      const params = toApiParams(filtersSnapshot, chipsSnapshot, pageNum, limit, extraParams);
      const result = await apiHandler(() => getUsersApi(params));
      if (result.success) {
        const data = unwrapData(result);
        setUsers(data?.users || []);
        applyPaginationMeta(data);
      } else {
        setError(result.message || 'Failed to load users');
        setUsers([]);
      }
      setLoading(false);
    },
    [filters, chips, page, limit, onboardingStatus, applyPaginationMeta, setLoading]
  );

    useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search) {
      const sp = new URLSearchParams(window.location.search);
      setFilters((prev) => ({
        ...prev,
        ...(sp.has('onboardingStatus') ? { onboardingStatus: sp.get('onboardingStatus') } : {}),
        ...(sp.has('plan') ? { plan: sp.get('plan') } : {}),
        ...(sp.has('verification') ? { verification: sp.get('verification') } : {}),
        ...(sp.has('sort') ? { sort: sp.get('sort') } : {}),
        ...(sp.has('search') ? { search: sp.get('search') } : {}),
      }));
    }
  }, []);

useEffect(() => {
    async function loadOptions() {
      const result = await apiHandler(() => getUserFilterOptionsApi());
      if (result.success) {
        setFilterOptions(unwrapData(result));
      }
    }
    loadOptions();
  }, []);

  useEffect(() => {
    async function markSeen() {
      await apiHandler(() => markUsersSeenApi());
      refreshBadges();
    }
    markSeen();
  }, [refreshBadges]);

  useEffect(() => {
    fetchUsers(page);
  }, [page]);

  function applyFilters() {
    if (page !== 1) {
      resetPage();
      fetchUsers(1, filters, chips);
    } else {
      fetchUsers(1, filters, chips);
    }
  }

  function handleFilterChange(e) {
    const { name, value } = e.target;
    const nextFilters = { ...filters, [name]: value };
    setFilters(nextFilters);
    if (page !== 1) resetPage();
    fetchUsers(1, nextFilters, chips);
  }

  function handleSearchChange(e) {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  }

  function resetFilters() {
    const clearedFilters = defaultFilters();
    const clearedChips = { ...DEFAULT_CHIPS };
    setFilters(clearedFilters);
    setChips(clearedChips);
    resetPage();
    fetchUsers(1, clearedFilters, clearedChips);
  }

  async function handleExport() {
    const params = toApiParams(filters, chips, 1, limit, { onboardingStatus });
    delete params.page;
    delete params.limit;
    const result = await apiHandler(() => exportUsersApi(params));
    if (result.success && result.data) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFilename;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Export started', 'success');
    } else {
      showToast(result.message || 'Export failed', 'error');
    }
  }

  return {
    users,
    filters,
    filterOptions,
    error,
    toast,
    loading,
    total,
    paginationProps,
    applyFilters,
    handleFilterChange,
    handleSearchChange,
    resetFilters,
    handleExport,
    refetchUsers: () => fetchUsers(page),
    showToast,
  };
}
